import React from 'react';
import { Box, Typography, Button, IconButton, Chip, Link } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {Edit, Delete, Add, PersonAdd, CalendarToday, FileDownload } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import ModalCourse from './ModalCourse';
import ModalResult from '../../components/ModalResult';
import ModalConfirm from '../../components/ModalConfirm';
import ModalCourseEnrollment from './ModalCourseEnrollment';
import { useAdmin } from '../../contexts/AdminContext';
import { exportCoursesToCSV } from '../../utils/exportCSV/exportCourses';

const CourseManagement = () => {
  const { courses, enrollments, members, deleteCourse } = useAdmin();
  const navigate = useNavigate();

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return dayjs(date).locale('it').format('DD-MMM-YY').toLowerCase();
  };

  const formatWeekdays = (weekdays) => {
    if (!weekdays || !Array.isArray(weekdays) || weekdays.length === 0) return 'N/A';
    
    const dayNames = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
    return weekdays.map(day => dayNames[day]).join(', ');
  };
  
  const [modal, setModal] = React.useState({ open: false, mode: 'add', course: null });
  const handleOpenModal = (mode, course = null) => setModal({ open: true, mode, course });
  const handleCloseModal = () => setModal({ open: false, mode: 'add', course: null });

  const [enrollmentModal, setEnrollmentModal] = React.useState({ open: false, course: null });
  const handleOpenEnrollmentModal = (course) => setEnrollmentModal({ open: true, course });
  const handleCloseEnrollmentModal = () => setEnrollmentModal({ open: false, course: null });

  const [resultModal, setResultModal] = React.useState({ open: false, message: '' });
  const [confirmModal, setConfirmModal] = React.useState({ open: false, course: null });

  const handleShowResult = (message) => setResultModal({ open: true, message });
  const handleCloseResult = () => setResultModal({ open: false, message: '' });

  const handleDeleteCourse = async () => {
    if (!confirmModal.course) return;
    try {
      const result = await deleteCourse(confirmModal.course._id);
      if (result.success) {
        handleShowResult('Corso eliminato con successo!');
      } else {
        handleShowResult('Errore durante la cancellazione del corso');
      }
    } catch {
      handleShowResult('Errore durante la cancellazione del corso');
    } finally {
      handleCloseConfirm();
    }
  };

  const handleOpenConfirm = (course) => setConfirmModal({ open: true, course });
  const handleCloseConfirm = () => setConfirmModal({ open: false, course: null });

  const handleEnrollmentSuccess = async (enrollment) => {
    // Il Context gestisce automaticamente l'aggiornamento
    handleShowResult('Partecipante aggiunto con successo! *** Se vuoi associare un PAGAMENTO a questa iscrizione vai a Pagamenti Corsi ***');
  };

  const handleCourseClick = (courseId) => {
    if (courseId) {
      navigate(`/teacher/course/${courseId}`);
    }
  };

  const handleExportCSV = () => {
    exportCoursesToCSV(courses);
  };

  const courseColumns = [
    { 
      field: 'name', 
      headerName: 'Nome Corso', 
      flex: 1, 
      minWidth: 160, 
      resizable: true,
      renderCell: (params) => {
        const courseName = params.value || 'N/A';
        
        if (courseName !== 'N/A') {
          return (
            <Link
              component="button"
              variant="body2"
              onClick={() => handleCourseClick(params.row._id)}
              sx={{
                color: '#1976d2', // Classic blue link color
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  color: '#1565c0', // Darker blue on hover
                  textDecoration: 'underline'
                }
              }}
            >
              {courseName}
            </Link>
          );
        }
        return courseName;
      }
    },
    { field: 'venue', headerName: 'Sede', flex: 1, minWidth: 120, resizable: true },
    {
      field: 'minLevel',
      headerName: 'Anno',
      flex: 0.8,
      minWidth: 120,
      resizable: true,
      renderCell: (params) => {
        if (!params.value) {
          // return <Chip label='Accesso libero' color='success' size='small' />;
          return 'Libero';
        }
        if (params.value >= 6) {
          return 'Oltre il 5° anno';
        }
        return `${params.value}° anno`;
      },
    },
    {
      field: 'weekdays',
      headerName: 'Giorni',
      flex: 0.8,
      minWidth: 120,
      resizable: true,
      renderCell: (params) => formatWeekdays(params.value),
    },
    // {
    //   field: 'isMembershipRequired',
    //   headerName: 'Tessera',
    //   flex: 0.6,
    //   minWidth: 80,
    //   resizable: true,
    //   renderCell: (params) => {
    //     if (params.value) {
    //       return <Chip label='Richiesta' color='warning' size='small' />;
    //     }
    //     return <Chip label='No' color='success' size='small' />;
    //   },
    // },
    {
      field: 'posti',
      headerName: 'Posti',
      flex: 0.7,
      minWidth: 80,
      resizable: true,
      renderCell: (params) => `${params.row.occupiedSeats}/${params.row.availableSeats}`,
    },
    {
      field: 'teacher',
      headerName: 'Docente',
      flex: 1,
      minWidth: 140,
      resizable: true,
      valueGetter: (teacher ) => {
        // console.log(value);
        if (!teacher) {
          return 'AAA - Da assegnare'; // ZZZ per far apparire alla fine
        }
        const name = teacher.name || '';
        const surname = teacher.surname || '';
        return `${surname} ${name}`; // Cognome Nome per ordinamento alfabetico
      },
      renderCell: (params) => {
        if (!params.row.teacher) {
          return <Chip label='Da assegnare' color='warning' size='small' />;
        }
        const name = params.row.teacher.name || '';
        const surname = params.row.teacher.surname || '';
        const nameInitial = name.charAt(0).toUpperCase() + '.';
        return (
          <span>
            {surname} {nameInitial}
          </span>
        );
      },
    },
    {
      field: 'startDate',
      headerName: 'Data Inizio',
      flex: 0.8,
      minWidth: 110,
      resizable: true,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'endDate',
      headerName: 'Data Fine',
      flex: 0.8,
      minWidth: 110,
      resizable: true,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'status',
      headerName: 'Stato',
      flex: 0.7,
      minWidth: 100,
      resizable: true,
      renderCell: (params) => <Chip label={params.value === 'published' ? 'Pubblicato' : 'Bozza'} color={params.value === 'published' ? 'success' : 'warning'} size='small' />,
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      flex: 1,
      minWidth: 120,
      resizable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {/* <IconButton size='small' color='primary' onClick={() => handleOpenModal('view', params.row)}>
              <Visibility />
            </IconButton> */}
            <IconButton size='small' color='primary' onClick={() => handleOpenModal('edit', params.row)}>
              <Edit />
            </IconButton>
            <IconButton size='small' color='info' onClick={() => navigate(`/teacher/course/${params.row._id}`)}>
              <CalendarToday />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size='small' color='success' onClick={() => handleOpenEnrollmentModal(params.row)}>
              <PersonAdd />
            </IconButton>
            <IconButton size='small' color='error' onClick={() => handleOpenConfirm(params.row)}>
              <Delete />
            </IconButton>
          </Box>
        </Box>
      ),
    },
  ];

  // console.log('courses', courses);
  return (
    <Box padding={0}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h6'>Gestione Corsi</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant='outlined' startIcon={<FileDownload />} onClick={handleExportCSV} disabled={!courses || courses.length === 0}>
            Esporta CSV
          </Button>
          <Button variant='contained' startIcon={<Add />} onClick={() => handleOpenModal('add')}>
            Aggiungi Corso
          </Button>
        </Box>
      </Box>
      <DataGrid 
        rows={courses} 
        columns={courseColumns} 
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
        }}
        rowsPerPageOptions={[10, 25, 50]}
        disableSelectionOnClick 
        hideFooterPagination={courses.length <= 10}
        // getRowHeight={() => '180px'}
        rowHeight={85}
        sx={{
          '& .MuiDataGrid-row': {
            alignItems: 'center'
          }
        }}
      />
      <ModalCourse
        open={modal.open}
        onClose={handleCloseModal}
        mode={modal.mode}
        course={modal.course}
        enrollments={enrollments}
        onSuccess={async () => {
          handleShowResult(modal.mode === 'add' ? 'Corso aggiunto con successo!' : 'Corso modificato con successo!');
        }}
        onEnrollmentAction={(action) => {
          switch (action) {
            case 'suspend':
              handleShowResult('Iscrizione sospesa con successo.');
              break;
            case 'unsuspend':
              handleShowResult('Iscrizione riattivata con successo.');
              break;
            case 'remove':
              handleShowResult('Iscrizione rimossa con successo.');
              break;
            default:
              break;
          }
        }}
      />
      <ModalResult open={resultModal.open} onClose={handleCloseResult} message={resultModal.message} />
      <ModalConfirm open={confirmModal.open} onClose={handleCloseConfirm} onConfirm={handleDeleteCourse} onCancel={handleCloseConfirm} title='Conferma eliminazione' description={`Sei sicuro di voler eliminare il corso "${confirmModal.course?.name}"?`} />
      <ModalCourseEnrollment
        open={enrollmentModal.open}
        onClose={handleCloseEnrollmentModal}
        course={enrollmentModal.course}
        users={members
          .filter((member) => {
            // Check if user is already enrolled in this course
            if (!enrollmentModal.course?._id) return true; // If no course ID, show all users
            return !enrollments?.some((enrollment) => enrollment.userId === member.userId && enrollment.courseId === enrollmentModal.course._id);
          })
          .map((member) => member.user)}
        onSuccess={handleEnrollmentSuccess}
      />
    </Box>
  );
};

export default CourseManagement;
